
import { Component, Vue, Prop, Watch } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"

import vSelect from "vue-select"
import Tags from "@/components/Fields/Tags.vue"
import AreaSelect from "@/components/Fields/AreaSelect.vue"
import FormGroup from "../Fields/FormGroup.vue"

import { Template, Area, Status, Severity, CaseDetails, TemplateDetails } from "@/types"

import TextAreaToggleExpand from "@/components/Fields/TextAreaToggleExpand.vue"
import { CurrentUser } from "@/types/user"

import api from "@/services/api"
const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		"text-area-expand": TextAreaToggleExpand,
		"tags": Tags,
		"area-select": AreaSelect,
		"form-group": FormGroup
	}
})
export default class CaseDetailsHeader extends Vue {
	// ----- Vuex ----- //

	@Getter("getMe")
	public me!: CurrentUser;

	@caseStore.Action("setFieldLockPolling")
	private setFieldLockPolling!: ({ caseDetails, fieldId }: { caseDetails: CaseDetails, fieldId: string })=> void;

	@caseStore.Action("cancelFieldLockPolling")
	private cancelFieldLockPolling!: ({ caseDetails, fieldId }: { caseDetails: CaseDetails, fieldId: string }) => void;

	@caseStore.Action public linkCase!: (arg0: any) => void;
	@caseStore.Action private saveCaseProperty!: ({ caseColumnKey, newValue }: { caseColumnKey: string, newValue: string | null }) => Promise<void>;

	@caseStore.Getter("getStatuses") public statuses!: Status[];
	@caseStore.Getter("getSeverities") public severities!: Severity[];

	@caseStore.Getter("getCreatableCategories") public categories!: Template[] | null;
	@caseStore.State("areas") public areas!: Area[];

	// ----- Props ----- //

	@Prop()
	public currentCase!: CaseDetails

	@Prop()
	public template!: TemplateDetails

	@Prop({ default: false })
	public canEditCurrentCase!: boolean

	@Prop({ default: true })
	public titleTemplateEnabled!: boolean

	@Prop({ default: "Case" })
	public caseName!: string

	@Prop({ default: "Record" })
	public recordName!: string

	@Prop({ default: false })
	public showAreas!: boolean

	@Prop({ default: false })
	public showStatus!: boolean

	@Prop({ default: false })
	public showTags!: boolean

	@Prop({ default: false })
	public showSeverity!: boolean

	@Prop({ default: false })
	public preventTemplateChange!: boolean

	// ----- Data Properties ----- //

	/**
	 * Dictionary of fields with changes currently being saved
	 * */
	private savingFields: { [key: string]: boolean } = {
		"summary": false
	}

	private pendingSaveTitle: string = this.currentCase.title

	// ----- Computed Properties ----- //

	/**
	 * Map of built in fields user has lock on
	 */
	private get ownedLocks (): { [key: string]: boolean } {
		if (!this.currentCase || !this.currentCase.builtInFieldLocks) {
			return {}
		}

		return Object.keys(this.currentCase!.builtInFieldLocks!)
			.filter(key => this.currentCase!.builtInFieldLocks![key] && this.currentCase!.builtInFieldLocks![key]!.id === this.me!.id)
			.reduce((acc: { [key: string]: boolean }, key: string) => {
				acc[key] = true
				return acc
			}, {} as { [key: string]: boolean })
	}

	// ----- Watchers ----- //

	@Watch("currentCase.title")
	private onTitleChanged (newValue: string): void {
		this.pendingSaveTitle = newValue
	}

	// ----- Methods ----- //

	/**
	 * Emits changes made to currentCase to parent component and waits for parent to resolve
	 * */
	private async onCaseChanged (): Promise<void> {
		this.$emit("caseChanged")
	}

	/**
	 * Updates title for currentCase
	*/
	private async updateTitle (): Promise<void> {
		this.currentCase.title = this.pendingSaveTitle
		await this.fieldLostFocus("title", this.currentCase.title)
	}

	private async onCaseCategoryChanged (templateId: string): Promise<void> {
		this.$emit("templateIdChanged", templateId)
	}

	private async fieldGotFocus (fieldId: string): Promise<void> {
		if (this.currentCase!.id && this.canEditCurrentCase) {
			await api.selectCaseField(this.currentCase!.id, fieldId, true)
			this.setFieldLockPolling({ caseDetails: this.currentCase!, fieldId: fieldId })
		}
	}

	private async fieldLostFocus (fieldId: string, newValue: string): Promise<void> {
		if (this.currentCase!.id && this.canEditCurrentCase) {
			try {
				this.savingFields[fieldId] = true
				await this.saveCaseProperty({ caseColumnKey: fieldId, newValue })
			} finally {
				this.savingFields[fieldId] = false
			}
			this.cancelFieldLockPolling({ caseDetails: this.currentCase, fieldId: fieldId })
		}
	}
}
